import React, { useCallback, forwardRef } from 'react';
import { Pressable, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getColorMetadata } from '../color-picker/utils/index.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '../option-color-generator/index.tsx';
import type { Props } from './types.tsx';

export const ColoredTag = forwardRef<HTMLButtonElement, Props>(
	(
		{
			labelName,
			href,
			actionSubjectId,
			pressableXcss,
			contentXcss,
			analyticsAttributes,
			color,
			...restProps
		},
		ref,
	) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { backgroundColor: borderColor } = getColorMetadata(
			memoizeGetAutoGeneratedColorForLabels(labelName),
		);

		const isPressable = fg('colored_tag_pressable_by_href') ? !!href : true;

		const onHandleClick = useCallback(() => {
			if (href) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.href = href;
			}
			if (actionSubjectId) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'link',
						attributes: analyticsAttributes,
					}),
					actionSubjectId,
				);
			}
		}, [actionSubjectId, createAnalyticsEvent, href, analyticsAttributes]);

		return isPressable ? (
			<Pressable
				ref={ref}
				xcss={[tagStyles, hoverStyles, ...(pressableXcss ? [pressableXcss] : [])]}
				onClick={onHandleClick}
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{ borderColor: color || borderColor }}
				{...restProps}
			>
				<Box xcss={[tagTextStyles, ...(contentXcss ? [contentXcss] : [])]}>{labelName}</Box>
			</Pressable>
		) : (
			<Box
				ref={ref}
				xcss={[tagStyles, tagTextStyles, ...(contentXcss ? [contentXcss] : [])]}
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{ borderColor: color || borderColor }}
			>
				{labelName}
			</Box>
		);
	},
);

const tagStyles = xcss({
	display: 'flex',
	backgroundColor: 'color.background.neutral.subtle',
	borderStyle: 'solid',
	borderWidth: '1px',
	borderRadius: token('border.radius.100'),
	paddingInline: 'space.050',
	paddingBlock: '0',
	font: 'font.body',
	marginBlock: 'space.050',
	marginInline: 'space.050',
	height: '20px',
	alignItems: 'center',
	maxWidth: '100%',
});

const hoverStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
		textDecoration: 'underline',
	},
});

const tagTextStyles = xcss({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	maxWidth: '180px',
});
